import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LoginIcon from "@mui/icons-material/Login";

import Logo from "../../images/logo/chinook_blue-logo-only.png";
import LogoWhite from "../../images/logo/logo-white.png";
import { StyleContext } from "../../context/styleContext";
import { useEmsSidePanelContext } from "../../context/EmsSidePanelContext";
import TreeViewDrawer from "../Drawers/TreeViewDrawer";
import ProjectsTreeSection from "./ProjectsTreeSection";
import { ALL_PLANT_MODULE_NAMES } from "../../constants/appModules";
import useModuleAccess from "../../hooks/useModuleAccess";
import ThemeToggle from "../ThemeToggle";
import EdgeMentorNavbarToggle from "../../pages/client/AllEquipments/CommonFeatures/Edge Mentor/EdgeMentorNavbarToggle";
import UserAvatar from "../MainNavs/UserAvatar";

const COLLAPSED_WIDTH = 68;

// `hideProjectsTree` is for the full-page EMS explorer, which already
// renders this same tree as its own left column - showing it here too would
// just be two copies of the same tree side by side.
/**
 * The app's primary navigation shell — mounted once in Layout (see
 * routing/GlobalOutlet.jsx), replacing the old top HorizontalNavbar. This is
 * deliberately just the Plant -> WBS -> Documents tree (ProjectsTreeSection)
 * plus its per-project nine-dot module launcher — the whole app is reached
 * by drilling into a project here, not through a separate list of links.
 *
 * `open` (from EmsSidePanelContext) means "expanded" rather than "visible" —
 * the sidebar stays mounted at all times, just narrower (an icon-only rail)
 * when collapsed, since it's the only way to navigate the app.
 */
export default function AppSidebar({ hideProjectsTree = false }) {
  const { colorMode } = useContext(StyleContext);
  const isDark = colorMode === "dark";
  const { open, setOpen } = useEmsSidePanelContext();
  const navigate = useNavigate();
  const { user, isAdmin, hasAnyModule } = useModuleAccess();

  const isLoggedIn = Boolean(user?.id);
  const showProjectsTree =
    !hideProjectsTree && (isAdmin || hasAnyModule(ALL_PLANT_MODULE_NAMES));

  return (
    <TreeViewDrawer open width={open ? undefined : COLLAPSED_WIDTH}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center",
            px: open ? 2 : 1,
            py: 2,
          }}
        >
          <a
            href="/"
            style={{ display: "flex", alignItems: "center", minWidth: 0 }}
          >
            <img
              alt="logo"
              src={isDark ? LogoWhite : Logo}
              style={{
                width: open ? 100 : 32,
                height: "auto",
                display: "block",
              }}
            />
          </a>
          {isLoggedIn && (
            <IconButton
              size="small"
              onClick={() => setOpen((v) => !v)}
              title={open ? "Collapse" : "Expand"}
            >
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          )}
        </Box>
        <Divider />

        {isLoggedIn && open && showProjectsTree ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <ProjectsTreeSection />
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}

        <Divider />
        {isLoggedIn ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: open ? "row" : "column",
              gap: 0.75,
              px: 1,
              py: 1.25,
            }}
          >
            <ThemeToggle />
            <EdgeMentorNavbarToggle />
            {open && <Box sx={{ flex: 1 }} />}
            <UserAvatar />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: open ? "row" : "column",
              alignItems: "center",
              justifyContent: open ? "space-between" : "center",
              gap: 1,
              px: open ? 2 : 1,
              py: 1.5,
            }}
          >
            <ThemeToggle />
            <Tooltip title={open ? "" : "Login"} placement="right">
              <IconButton color="primary" onClick={() => navigate("/login")}>
                {open ? (
                  <Typography variant="button" sx={{ px: 1 }}>
                    Login
                  </Typography>
                ) : (
                  <LoginIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </TreeViewDrawer>
  );
}
